import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { animalDetailQuery } from '@/lib/queries';
import type { Role } from '@/lib/schemas';

interface AnimalDetailPanelProps {
  animalId: string | null;
  onClose: () => void;
  role: Role;
}

const STATUS_COLORS: Record<string, string> = {
  Milking: 'bg-ndd-600',
  Dry: 'bg-amber-600',
  'Close-up': 'bg-purple-600',
  Heifer: 'bg-blue-600',
  Calf: 'bg-cyan-600',
  Dead: 'bg-gray-600',
  Sold: 'bg-gray-500',
};

function InfoRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="flex items-start justify-between py-1.5 border-b border-ndd-800 last:border-0">
      <span className="text-xs text-ndd-400">{label}</span>
      <span className="text-sm text-white">{value ?? '—'}</span>
    </div>
  );
}

export function AnimalDetailPanel({ animalId, onClose, role }: AnimalDetailPanelProps) {
  const { data: animal, isLoading } = useQuery({
    ...animalDetailQuery(animalId ?? ''),
    enabled: !!animalId,
  });

  const isFarmer = role === 'farmer';

  return (
    <Sheet
      open={!!animalId}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent side="right" className="w-[400px] flex flex-col p-0">
        <SheetHeader className="p-4">
          <div className="flex items-center justify-between">
            <SheetTitle>
              {isLoading
                ? 'Loading…'
                : animal
                  ? `${animal.animal_id}${animal.name ? ` — ${animal.name}` : ''}`
                  : 'Animal'}
            </SheetTitle>
            <button type="button" onClick={onClose} className="text-ndd-400 hover:text-white">
              <X size={20} />
            </button>
          </div>
          {animal && (
            <div className="flex items-center gap-2 mt-1">
              <Badge className={`text-white ${STATUS_COLORS[animal.status] ?? 'bg-gray-600'}`}>
                {animal.status}
              </Badge>
              {animal.dim != null && (
                <span className="text-sm text-ndd-400">DIM: {animal.dim}</span>
              )}
            </div>
          )}
        </SheetHeader>

        {isLoading && (
          <div className="flex-1 flex items-center justify-center text-ndd-400">Loading…</div>
        )}

        {animal && (
          <ScrollArea className="flex-1 px-4 py-2">
            {/* Key Info */}
            <div className="mb-4">
              <h3 className="text-xs font-semibold text-ndd-400 uppercase mb-2">Key Info</h3>
              <div className="rounded-lg bg-ndd-900/50 px-3">
                <InfoRow label="Barn Group" value={animal.barn_group} />
                <InfoRow label="Lactation" value={animal.lactation_number} />
                <InfoRow label="Sire" value={animal.sire} />
                <InfoRow label="Birth Date" value={animal.birth_date} />
              </div>
            </div>

            {/* Breeding */}
            <div className="mb-4">
              <h3 className="text-xs font-semibold text-ndd-400 uppercase mb-2">Breeding</h3>
              <div className="rounded-lg bg-ndd-900/50 px-3">
                <InfoRow label="Preg Status" value={animal.preg_status} />
                <InfoRow label="Last Bred" value={animal.last_breeding_date} />
                <InfoRow label="Due Date" value={animal.expected_calving_date} />
                {animal.days_until_calving != null && (
                  <InfoRow label="Days Until Calving" value={animal.days_until_calving} />
                )}
              </div>
            </div>

            {/* Event Timeline */}
            <div className="mb-4">
              <h3 className="text-xs font-semibold text-ndd-400 uppercase mb-2">Event History</h3>
              {animal.events.length === 0 ? (
                <p className="text-sm text-ndd-500">No events recorded</p>
              ) : (
                <div className="space-y-2">
                  {animal.events.map((ev) => (
                    <div key={ev.event_id} className="rounded-lg bg-ndd-900/50 px-3 py-2 text-sm">
                      <div className="flex items-start justify-between">
                        <span className="font-medium text-ndd-200 capitalize">
                          {ev.event_type.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-ndd-500">{ev.event_date}</span>
                      </div>
                      {ev.result && <p className="text-xs text-ndd-400 mt-0.5">{ev.result}</p>}
                      {ev.notes && <p className="text-xs text-ndd-500 mt-0.5">{ev.notes}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>
        )}

        {/* Quick Actions */}
        {isFarmer && animal && (
          <div className="border-t border-ndd-800 p-4 flex gap-3">
            <Button variant="outline" size="sm" className="flex-1">
              Edit
            </Button>
            <Button variant="outline" size="sm" className="flex-1">
              Log Event
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
